import { Dialog, Pagination as MuiPagination } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  isArticleListedOnPublicFeed,
  type TLatestNewsItem,
} from './latestNewsStorage';
import { useLatestNewsFilter } from './LatestNewsFilterContext';
import {
  LATEST_NEWS_COMPACT_RESOURCE_FILTERS,
  LATEST_NEWS_PHASE2_ENABLED,
} from './latestNewsFeatures';
import { ToTopButton } from '~/components/ToTopButton/ToTopButton';
import { useArticlesPublicGetArticlesMutation } from '~/api/oneBoxV3';
import {
  articleDtoToLatestNewsItem,
  defaultArticlePagination,
} from './latestNewsArticleMap';
import { getterClientResourceFilter } from '~/store/resource/resourceGetter';
import { latestNewsReadMoreEvent } from '~/tracking';
import { LatestNewsAccordionCard } from './LatestNewsAccordionCard';
import {
  resourceLatestNews as latestNewsPage,
  ResourceLatestNewsIcons,
} from './ResourceLatestNews.style';
import type {
  TArticleEduCode,
  TArticleSearchDto,
} from '~/api/oneBoxV3/oneBoxV3.type';
import { youtubeEmbedId } from './latestNewsYoutube';

const PAGE_SIZE = 10;

/** 後台未上傳封面／無 YouTube 縮圖等時，左欄顯示的佔位圖（`public/assets/image/`） */
const LATEST_NEWS_FALLBACK_COVER_SRC = '/assets/image/news-none.png';

type TArticleVideoEmbed =
  { kind: 'youtube'; id: string } | { kind: 'iframe'; src: string };

const parseArticleVideoEmbed = (raw: string): TArticleVideoEmbed | null => {
  const u = raw?.trim();
  if (!u) return null;
  const yid = youtubeEmbedId(u);
  if (yid) return { kind: 'youtube', id: yid };
  const vim = u.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  if (vim?.[1]) {
    return { kind: 'iframe', src: `https://player.vimeo.com/video/${vim[1]}` };
  }
  if (/^https?:\/\//i.test(u)) return { kind: 'iframe', src: u };
  return null;
};

/**
 * 左欄縮圖：僅「Youtube 連結」欄位會改用 YouTube 預覽圖；
 * 有上傳封面時一律顯示封面（內文嵌入影片不影響左欄，避免誤判 iframe 路徑）。
 */
const coverSrc = (row: TLatestNewsItem): string => {
  const data = row.mediaDataUrl;
  if (data) {
    const isImage = row.mediaType === 'image' || data.startsWith('data:image/');
    if (isImage) return data;
  }
  if (row.coverImageUrl?.trim()) return row.coverImageUrl.trim();

  const fieldYid = youtubeEmbedId(row.youtubeUrl);
  if (fieldYid) return `https://img.youtube.com/vi/${fieldYid}/hqdefault.jpg`;

  if (row.seoOgImageUrl?.trim()) return row.seoOgImageUrl.trim();
  return LATEST_NEWS_FALLBACK_COVER_SRC;
};

/** 縮圖可點開預覽（排除影片檔、佔位圖） */
const canOpenImageLightbox = (thumb: string | null) => {
  if (!thumb) return false;
  if (thumb === LATEST_NEWS_FALLBACK_COVER_SRC) return false;
  if (thumb.startsWith('data:video') || thumb.startsWith('data:application')) {
    return false;
  }
  if (/\.(mp4|webm|m4v|ogg)(\?|$)/i.test(thumb)) return false;
  return true;
};

/** 前台依排序顯示全部附件（後台可建多筆） */
const sortedPublicAttachments = (
  row: TLatestNewsItem,
): { url: string; fileName: string; sizeBytes: number }[] => {
  if (!row.attachments?.length) return [];
  return [...row.attachments]
    .filter(a => Boolean(a.url?.trim()))
    .sort((a, b) => a.sort - b.sort)
    .map(a => ({
      url: a.url.trim(),
      fileName: a.fileName?.trim() || '附件下載',
      sizeBytes: a.sizeBytes || 0,
    }));
};

const buildArticleSearch = (params: {
  eduCode: string;
  subject: string;
  schoolYear: string;
  grade: string;
}): TArticleSearchDto | undefined => {
  const { eduCode, subject, schoolYear, grade } = params;
  const search: TArticleSearchDto = {};

  if (eduCode && eduCode !== 'all') {
    search.eduCodes = [eduCode as TArticleEduCode];
  }
  if (!LATEST_NEWS_COMPACT_RESOURCE_FILTERS) {
    if (subject && subject !== 'all') search.subjects = [subject];
    if (schoolYear && schoolYear !== 'all') search.academicYears = [schoolYear];
  }
  if (grade && grade !== 'all') search.grades = [grade];

  return Object.keys(search).length ? search : undefined;
};

type TLatestNewsCardProps = {
  row: TLatestNewsItem;
  expanded: boolean;
  onToggleExpand: () => void;
  thumb: string;
  attachments: ReturnType<typeof sortedPublicAttachments>;
  onImageLightbox: (src: string) => void;
  onVideoLightbox: (iframeSrc: string) => void;
};

const LatestNewsCard = ({
  row,
  expanded,
  onToggleExpand,
  thumb,
  attachments,
  onImageLightbox,
  onVideoLightbox,
}: TLatestNewsCardProps) => {
  /** 僅後台「Youtube 連結」欄位觸發封面播放；內文嵌入影片在展開內文內觀看 */
  const videoEmbed = parseArticleVideoEmbed(row.youtubeUrl || '');

  const onCoverClick =
    videoEmbed || (thumb && canOpenImageLightbox(thumb))
      ? () => {
          if (videoEmbed) {
            const iframeSrc =
              videoEmbed.kind === 'youtube'
                ? `https://www.youtube.com/embed/${videoEmbed.id}?autoplay=1`
                : `${videoEmbed.src}${videoEmbed.src.includes('?') ? '&' : '?'}autoplay=1`;
            onVideoLightbox(iframeSrc);
            return;
          }
          if (thumb) onImageLightbox(thumb);
        }
      : undefined;

  return (
    <LatestNewsAccordionCard
      row={row}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      thumb={thumb}
      attachments={attachments}
      onCoverClick={onCoverClick}
    />
  );
};

/**
 * 僅於 /resource 且側欄選「最新消息」時顯示（前台瀏覽）。
 * 卡片排版：左側縮圖＋標籤＋日期；右側淺藍標題列、摘要、附件、繼續閱讀。
 */
export const ResourceLatestNews = () => {
  const [items, setItems] = useState<TLatestNewsItem[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  /** 點擊左側縮圖：同網址以完整比例顯示 */
  const [imageLightboxSrc, setImageLightboxSrc] = useState<string | null>(null);
  /** 點擊左側縮圖（YouTube/影片）：放大視窗播放 */
  const [videoLightboxSrc, setVideoLightboxSrc] = useState<string | null>(null);
  const [publicGetArticles] = useArticlesPublicGetArticlesMutation();

  /** 最新消息篩選：獨立於左側資源搜尋條件（可任意跳選任意欄位） */
  const { tagParent, tagKeyword } = useLatestNewsFilter();
  // 與左側資源搜尋共用同一份 Redux 條件（電子書 ↔ 最新消息切換時沿用）
  const { eduCodes, subjects, years, grades } = useSelector(
    getterClientResourceFilter,
  );
  const eduCode = (eduCodes as string) || 'all';
  const subject = (subjects as string) || 'all';
  const schoolYear = (years as string) || 'all';
  const grade = (grades as string) || 'all';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await publicGetArticles({
          // 避免中介層快取導致前台看到舊資料
          snapshotTime: new Date().toISOString(),
          search: buildArticleSearch({ eduCode, subject, schoolYear, grade }),
          pagination: {
            ...defaultArticlePagination(),
            page,
            pageSize: PAGE_SIZE,
          },
        }).unwrap();

        if (cancelled) return;
        if (res.err !== 0 || !res.data?.data) {
          setItems([]);
          setTotalPages(1);
          return;
        }

        setItems(res.data.data.map(articleDtoToLatestNewsItem));
        setTotalPages(Math.max(1, res.data.totalPages || 1));
      } catch {
        if (!cancelled) {
          setItems([]);
          setTotalPages(1);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [publicGetArticles, eduCode, subject, schoolYear, grade, page]);

  useEffect(() => {
    setPage(1);
  }, [tagParent, tagKeyword, eduCode, subject, schoolYear, grade]);

  const filteredOnPage = useMemo(() => {
    const kw = tagKeyword.trim().toLowerCase();
    return [...items]
      .filter(a =>
        isArticleListedOnPublicFeed(a, Date.now(), {
          enforceTakeDown: LATEST_NEWS_PHASE2_ENABLED,
        }),
      )
      .filter(a => {
        if (!LATEST_NEWS_PHASE2_ENABLED) return true;
        if (tagParent !== '全部') {
          const hit = a.tags.some(t => t.includes(tagParent));
          if (!hit) return false;
        }
        if (kw) {
          const inTags = a.tags.some(t => t.toLowerCase().includes(kw));
          if (!inTags) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const pa = (a.publishAt || `${a.publishedAt}T00:00:00`).replace(
          / /g,
          'T',
        );
        const pb = (b.publishAt || `${b.publishedAt}T00:00:00`).replace(
          / /g,
          'T',
        );
        const ta = Date.parse(pa);
        const tb = Date.parse(pb);
        const sa = Number.isFinite(ta) ? ta : 0;
        const sb = Number.isFinite(tb) ? tb : 0;
        if (sb !== sa) return sb - sa;
        if (LATEST_NEWS_PHASE2_ENABLED) {
          const assistA = a.sortAssist ?? 0;
          const assistB = b.sortAssist ?? 0;
          if (assistB !== assistA) return assistB - assistA;
        }
        return b.id.localeCompare(a.id);
      });
  }, [items, tagParent, tagKeyword]);

  const toggleExpand = (id: string) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  return (
    <latestNewsPage.PageRoot>
      <Dialog
        open={Boolean(imageLightboxSrc || videoLightboxSrc)}
        onClose={() => {
          setImageLightboxSrc(null);
          setVideoLightboxSrc(null);
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: latestNewsPage.LightboxPaperSx,
        }}
        BackdropProps={{
          sx: latestNewsPage.LightboxBackdropSx,
        }}
      >
        <latestNewsPage.LightboxStage>
          <latestNewsPage.LightboxClose
            aria-label="關閉預覽"
            onClick={() => {
              setImageLightboxSrc(null);
              setVideoLightboxSrc(null);
            }}
          >
            <ResourceLatestNewsIcons.Close />
          </latestNewsPage.LightboxClose>
          {imageLightboxSrc && !videoLightboxSrc && (
            <latestNewsPage.LightboxImage src={imageLightboxSrc} alt="" />
          )}
          {videoLightboxSrc && (
            <latestNewsPage.LightboxVideoOuter>
              <latestNewsPage.LightboxVideoInner>
                <latestNewsPage.LightboxIframe
                  title="影片"
                  src={videoLightboxSrc}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                />
              </latestNewsPage.LightboxVideoInner>
            </latestNewsPage.LightboxVideoOuter>
          )}
        </latestNewsPage.LightboxStage>
      </Dialog>

      {filteredOnPage.length === 0 ? (
        <latestNewsPage.EmptyPaper elevation={0}>
          <latestNewsPage.EmptyText>
            目前尚未有最新消息，敬請期待。
          </latestNewsPage.EmptyText>
        </latestNewsPage.EmptyPaper>
      ) : (
        <latestNewsPage.ListStack>
          {filteredOnPage.map(row => {
            const expanded = expandedId === row.id;
            const thumb = coverSrc(row);
            const attachments = sortedPublicAttachments(row);

            return (
              <LatestNewsCard
                key={row.id}
                row={row}
                expanded={expanded}
                onToggleExpand={() => {
                  if (!expanded) {
                    latestNewsReadMoreEvent({
                      news_title: row.title,
                      edu: eduCode,
                      subject,
                      year: schoolYear,
                      grade,
                    });
                  }
                  toggleExpand(row.id);
                }}
                thumb={thumb}
                attachments={attachments}
                onImageLightbox={setImageLightboxSrc}
                onVideoLightbox={setVideoLightboxSrc}
              />
            );
          })}
        </latestNewsPage.ListStack>
      )}

      {filteredOnPage.length > 0 && totalPages > 1 && (
        <latestNewsPage.PaginationStack>
          <MuiPagination
            count={totalPages}
            page={Math.min(page, totalPages)}
            onChange={(_, p) => {
              setPage(p);
              setExpandedId(null);
            }}
            color="primary"
            shape="rounded"
          />
        </latestNewsPage.PaginationStack>
      )}

      <ToTopButton />
    </latestNewsPage.PageRoot>
  );
};
